import { InfoCircleOutlined } from "@ant-design/icons";
import { InputNumber, Modal, Typography } from "antd";
import Title from "antd/es/typography/Title";
import React, { useState } from "react";
import { UpdateRankAPI } from "../../API/UpdateRankAPI";
import { useFlightModel } from "../../contexts/FlightModelContext";
const { Text } = Typography;

// A factory, not a shared constant: formValidation() copies the state
// shallowly and writes to errorFieds, so a module-level object would be
// mutated and carry a stale "Rank is required" into the next open.
const createMoveRankState = () => ({ rank: "", errorFieds: { rank: "" } });

// InputNumber keeps whatever is typed in the box until it is parsed, so letters
// and symbols show up in the field even though they never become a value.
// Dropping them at the keystroke keeps the input digits-only.
// Ranks start at 1, so a leading zero is never valid. Relying on InputNumber's
// `min` instead would clamp only on blur -- which is the same click that
// submits, so the box would silently jump from 0 to 1 as the move fired.
// A zero typed at the head of the field (empty box included) is rejected here.
const isLeadingZero = (event) =>
  event.key === "0" && event.target.selectionStart === 0;

// What the box would read if the keystroke (or paste) landed: the current text
// with the selected span -- empty when nothing is highlighted -- replaced by it.
// Comparing that, rather than the typed character alone, is what lets a digit
// through while the number is still small and stops it once it overshoots.
const valueAfterInput = (event, insertion) => {
  const { value, selectionStart, selectionEnd } = event.target;
  return value.slice(0, selectionStart) + insertion + value.slice(selectionEnd);
};

// maxRank comes from the rank list API, so until it has loaded (0) there is no
// ceiling to hold the input to and only the lower bound applies. Non-digit
// input parses to NaN, which is never greater than maxRank, so it falls through
// to the digit rules below rather than being caught here.
const exceedsMaxRank = (event, insertion, maxRank) =>
  maxRank > 0 && Number(valueAfterInput(event, insertion)) > maxRank;

const blockNonDigitKeys = (maxRank) => (event) => {
  // Editing / navigation keys and shortcuts (copy, paste, select all) are
  // multi-character key names or modified, so they pass through untouched.
  if (event.ctrlKey || event.metaKey || event.key.length > 1) return;

  if (
    !/^\d$/.test(event.key) ||
    isLeadingZero(event) ||
    exceedsMaxRank(event, event.key, maxRank)
  ) {
    event.preventDefault();
  }
};

// InputNumber reports null for an emptied box, NaN for an unparsable one and a
// number otherwise, so every "nothing usable was entered" case funnels here.
const isRankMissing = (rank) => !rank || Number.isNaN(rank);

// An empty box is the only way the field can still be wrong: the guards above
// keep anything over maxRank from being entered in the first place, so the
// ceiling is stated as a note beside the input rather than raised as an error.
const getRankError = (rank) =>
  isRankMissing(rank) ? "Rank is required" : "";

const blockNonDigitPaste = (maxRank) => (event) => {
  // Same bounds as the keystroke guard: digits only, never starting at 0, and
  // never landing the field above the highest rank the list actually has.
  const pasted = event.clipboardData.getData("text").trim();

  if (!/^[1-9]\d*$/.test(pasted) || exceedsMaxRank(event, pasted, maxRank)) {
    event.preventDefault();
  }
};

export const MoveRank = ({
  open,
  setOpen,
  selectedRow,
  listData,
  maxRank,
  getRankingDataFun,
  dragableState,
  sortBy,
  messageToast,
  setSelectedRowKeys,
  setSelectedRow,
}) => {
  const [inputElements, setInputElements] = useState(createMoveRankState);
  const { selectedFlightModel, refreshFlightModels } = useFlightModel();
  const canManualRerank = dragableState === true && sortBy === "rank";
  const manualRerankDisabledHint =
    dragableState !== true
      ? "To change the rank, you must clear all filters applied"
      : "Manual re-ranking is available only when the table is sorted by Rank";

  function formValidation() {
    // Emptying the box makes InputNumber report null, not "", so an equality
    // check against "" let a cleared field through and posted new_rank: null.
    // getRankError() is the same predicate the onChange handler uses, so the
    // button and the message always agree on what counts as a usable rank.
    const error = getRankError(inputElements.rank);

    setInputElements((prev) => ({
      ...prev,
      errorFieds: { ...prev.errorFieds, rank: error },
    }));

    return !error;
  }

  async function applyFun() {
    if (formValidation()) {
      if (!canManualRerank) {
        messageToast("warning", manualRerankDisabledHint);
        return;
      }

      if (
        selectedRow?.id &&
        selectedRow?.id !== "" &&
        selectedRow?.id !== null
      ) {
        const activeInd = listData.findIndex(
          (i) => i.rank === selectedRow?.rank,
        );
        const overInd = listData.findIndex(
          (i) => i.rank === inputElements.rank,
        );

        if (listData[activeInd]?.rank !== listData[overInd]?.rank) {
          let arr = [];
          if (activeInd > overInd) {
            for (let index = overInd; index < activeInd; index++) {
              const element = listData[index]?.spec_concat;
              arr.push(element);
            }
          } else {
            for (let index = activeInd + 1; index <= overInd; index++) {
              const element = listData[index]?.spec_concat;
              arr.push(element);
            }
          }
          let flightModelToUse = selectedFlightModel;

          // Fallback: fetch from context if not available
          if (!flightModelToUse) {
            const refreshed = await refreshFlightModels();
            flightModelToUse = refreshed?.selectedFlightModel || null;
          }
          if (!flightModelToUse) {
            messageToast("error", "No flight model selected.");
            return;
          }

          const bodyData = {
            flight_model: flightModelToUse,
            // localStorage.getItem("flight_model"),
            old_rank: listData[activeInd]?.rank,
            new_rank: inputElements?.rank,
            // listData[overInd]?.rank,
            spec_list: [],
            //  arr,
            selected_spec: listData[activeInd]?.spec_concat,
          };

          updateRankFun(bodyData);
        } else {
          messageToast("error", "Selected Ranks are same");
        }
      } else {
        messageToast("warning", "To Move the rank, you must select one Row");
      }
    }
  }

  function cancelFun() {
    setInputElements(createMoveRankState());
    setOpen(false);
  }

  // Only a completed move retires the modal. A rejected rank ("Rank must be
  // between 1 and 440") keeps it open with the value still in the box, so the
  // user can correct it instead of reselecting the row and retyping. The toast
  // carries the reason -- errorFieds stays for the required-field check alone.
  async function updateRankFun(bodyData) {
    await UpdateRankAPI.dragandupdateRank(bodyData)
      .then((res) => {
        if (res.data?.status === true) {
          setSelectedRowKeys([]);
          setSelectedRow({});
          getRankingDataFun();
          messageToast("success", res.data?.message);
          setInputElements(createMoveRankState());
          setOpen(false);
        } else {
          messageToast("error", res.data?.message);
          if (res.data?.reload === true) {
            setTimeout(() => {
              window.location.reload();
            }, "2000");
          }
        }
      })
      .catch((err) => {
        messageToast("error", "Internal Server Error");
      });
  }

  return (
    <Modal
      open={open}
      okText="Move"
      cancelText="Cancel"
      onCancel={() => cancelFun()}
      onOk={applyFun}
    >
      <div style={{ padding: "10px" }}>
        <div
          style={{ marginBottom: "20px", borderBottom: "solid 1px #80808047" }}
        >
          <Title level={5}>Move to</Title>
        </div>
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "6px" }}>Rank</label>
          <InputNumber
            status={!inputElements.errorFieds.rank ? "" : "error"}
            style={{ display: "block", width: "100%" }}
            min={1}
            precision={0}
            onKeyDown={blockNonDigitKeys(maxRank)}
            onPaste={blockNonDigitPaste(maxRank)}
            value={inputElements.rank}
            onChange={(e) => {
              const value = e;
              setInputElements((prev) => ({
                ...prev,
                rank: value,
                errorFieds: {
                  ...prev.errorFieds,
                  rank: getRankError(value),
                },
              }));
            }}
          />
          {/* One line, never both: the note explains what to type, so it steps
              aside while the error says what went wrong and returns once the
              field is valid again. The note also waits for the rank list to
              answer -- an unloaded ceiling reads as "1 and 0". */}
          {inputElements.errorFieds.rank ? (
            <Text
              type="danger"
              italic
              style={{ display: "block", marginTop: "6px" }}
            >
              {inputElements.errorFieds.rank}
            </Text>
          ) : (
            maxRank > 0 && (
              <Text
                type="secondary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginTop: "6px",
                  fontSize: "12px",
                }}
              >
                <InfoCircleOutlined />
                {`Rank must be between 1 and ${maxRank}`}
              </Text>
            )
          )}
        </div>
      </div>
    </Modal>
  );
};
